<template>
  <!--
    AB#21279 — the orders open in the detail pane, as tabs.

    One strip, top of the pane. Each tab is the order number and the merchant,
    with its own ×; the active one is white and joined to the pane below. The
    two controls at the right end are the pane's: expand to full width, and
    close the current tab. A counter says how many of the five are in use
    before anyone hits the ceiling.
  -->
  <div class="otab-strip">
    <!--
      Four tabs are shown at a time. With five open, the fifth was off the end
      of a scroll container with no scrollbar and no way to reach it. Now the
      strip is a window: ‹ › slide it, the active tab is always inside it, and
      the counter says what is open beyond the edge.
    -->
    <button
      v-if="tabs.length > VISIBLE"
      type="button"
      class="otab-nav"
      :disabled="windowStart === 0"
      title="Previous orders"
      aria-label="Show previous orders"
      @click="slide(-1)"
    >
      <ChevronLeftIcon class="size-4" aria-hidden="true" />
    </button>
    <div class="otab-list" role="tablist" aria-label="Open orders">
      <div
        v-for="tab in visibleTabs"
        :key="tab.key"
        role="tab"
        tabindex="0"
        class="otab"
        :class="{ 'otab--on': tab.key === activeKey }"
        :aria-selected="tab.key === activeKey"
        :title="tab.dirty ? `${tab.label} — unsaved changes` : tab.label"
        :data-key="tab.key"
        @click="emit('activate', tab.key)"
        @keydown.enter.prevent="emit('activate', tab.key)"
        @keydown.space.prevent="emit('activate', tab.key)"
        @keydown.left.prevent="step(tab.key, -1)"
        @keydown.right.prevent="step(tab.key, 1)"
        @keydown.delete.prevent="emit('close', tab.key)"
        @auxclick.middle.prevent="emit('close', tab.key)"
      >
        <span class="otab-no">{{ tab.label || '…' }}</span>
        <span v-if="tab.merchant" class="otab-merchant">{{ tab.merchant }}</span>
        <span v-if="tab.dirty" class="otab-dirty" aria-label="Unsaved changes"></span>
        <button
          type="button"
          class="otab-x"
          :aria-label="`Close ${tab.label}`"
          :title="`Close ${tab.label}`"
          @click.stop="emit('close', tab.key)"
        >
          <XMarkIcon class="size-3.5" aria-hidden="true" />
        </button>
      </div>
    </div>
    <button
      v-if="tabs.length > VISIBLE"
      type="button"
      class="otab-nav"
      :disabled="windowStart + VISIBLE >= tabs.length"
      title="Next orders"
      aria-label="Show next orders"
      @click="slide(1)"
    >
      <ChevronRightIcon class="size-4" aria-hidden="true" />
    </button>

    <span class="otab-count" :title="`${tabs.length} of ${max} orders open`">
      {{ tabs.length }} / {{ max }}
    </span>

    <div class="otab-ctl">
      <button
        type="button"
        class="dash-icon-btn otab-expand"
        :class="{ 'otab-expand--on': expanded }"
        :title="expanded ? 'Back to list (Esc)' : 'Expand'"
        :aria-label="expanded ? 'Back to list' : 'Expand order view'"
        :aria-pressed="expanded"
        @click="emit('toggle-expand')"
      >
        <ArrowsPointingInIcon v-if="expanded" class="size-5" aria-hidden="true" />
        <ArrowsPointingOutIcon v-else class="size-5" aria-hidden="true" />
      </button>
      <!-- Closes everything: every tab, and the pane with them. One order is
           closed from its own × on the tab; this × was doing the same job
           twice and leaving the pane open with the next tab, which read as
           "close did not work". -->
      <button
        type="button"
        class="dash-icon-btn"
        :title="tabs.length > 1 ? `Close all ${tabs.length} orders` : 'Close'"
        :aria-label="tabs.length > 1 ? `Close all ${tabs.length} orders` : 'Close'"
        data-test="otab-close-all"
        @click="emit('close-all')"
      >
        <XMarkIcon class="size-5" aria-hidden="true" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { MAX_ORDER_TABS, type OrderTab } from '@/utils/orderTabs';
import {
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
} from '@heroicons/vue/24/outline';
import { computed, ref, watch } from 'vue';

const props = defineProps<{
  tabs: OrderTab[];
  activeKey: string | null;
  expanded: boolean;
}>();

const emit = defineEmits<{
  (e: 'activate', key: string): void;
  (e: 'close', key: string): void;
  (e: 'close-all'): void;
  (e: 'toggle-expand'): void;
}>();

const max = MAX_ORDER_TABS;

/** How many tabs the strip shows at once. */
const VISIBLE = 4;
const windowStart = ref(0);

const maxStart = computed(() => Math.max(0, props.tabs.length - VISIBLE));

const visibleTabs = computed(() =>
  props.tabs.slice(windowStart.value, windowStart.value + VISIBLE)
);

const slide = (by: 1 | -1) => {
  windowStart.value = Math.min(maxStart.value, Math.max(0, windowStart.value + by));
};

// The active tab is always in the window: activating one beyond the edge —
// from a row click, Alt+digit, or the arrow keys — slides the window to it.
// Closing tabs shrinks the window back so it never shows empty space.
watch(
  [() => props.activeKey, () => props.tabs.length],
  () => {
    windowStart.value = Math.min(windowStart.value, maxStart.value);
    const index = props.tabs.findIndex((tab) => tab.key === props.activeKey);
    if (index < 0) return;
    if (index < windowStart.value) windowStart.value = index;
    else if (index >= windowStart.value + VISIBLE) windowStart.value = index - VISIBLE + 1;
  },
  { immediate: true }
);

/** Arrow keys move along the strip, and activate as they go. */
const step = (fromKey: string, by: 1 | -1) => {
  const index = props.tabs.findIndex((tab) => tab.key === fromKey);
  const next = props.tabs[index + by];
  if (next) emit('activate', next.key);
};
</script>

<style lang="scss">
.otab-strip {
  @apply flex items-end gap-1 h-[44px] px-2 pt-[6px] bg-app border-b border-line shrink-0;
}
.otab-list {
  @apply flex items-end gap-1 min-w-0 flex-1 overflow-hidden;
}
/* Four always fit: each tab shares the row and shrinks its merchant first. */
.otab {
  @apply relative flex items-center gap-[7px] h-[37px] px-[10px] pr-[6px] rounded-t-[8px]
         text-ink-soft cursor-pointer select-none whitespace-nowrap min-w-0
         border border-transparent border-b-0 transition-colors;
  flex: 1 1 0;
  max-width: 170px;
}
.otab-nav {
  @apply flex items-center justify-center w-[24px] h-[28px] mb-[5px] rounded-[6px]
         text-ink-soft bg-transparent border-0 cursor-pointer shrink-0;
}
.otab-nav:hover:not(:disabled) {
  @apply bg-surface-2 text-ink;
}
.otab-nav:disabled {
  @apply text-ink-mute cursor-default;
}
.otab:hover {
  @apply bg-surface-2;
}
.otab:focus-visible {
  @apply outline outline-2 -outline-offset-2 outline-[var(--accent)];
}
.otab--on,
.otab--on:hover {
  @apply bg-surface border-line;
  margin-bottom: -1px;
  padding-bottom: 1px;
}
.otab-no {
  @apply text-[12px] font-semibold text-ink;
  font-variant-numeric: tabular-nums;
}
.otab--on .otab-no {
  @apply text-[var(--accent)];
}
.otab-merchant {
  @apply text-[10.5px] text-ink-mute overflow-hidden text-ellipsis min-w-0;
  flex: 1 1 auto;
  max-width: 80px;
}
.otab-dirty {
  @apply inline-block w-[6px] h-[6px] rounded-full bg-[var(--warning)] shrink-0;
}
.otab-x {
  @apply flex items-center justify-center w-[20px] h-[20px] rounded-[5px] text-ink-mute
         bg-transparent border-0 cursor-pointer shrink-0;
}
.otab-x:hover {
  @apply bg-surface-2 text-ink;
}
.otab-count {
  @apply text-[10.5px] text-ink-mute px-2 pb-[10px] shrink-0;
  font-variant-numeric: tabular-nums;
}
.otab-ctl {
  @apply flex items-center gap-[2px] pb-[4px] shrink-0;
}
.otab-expand--on {
  @apply bg-surface-2 text-ink;
}
</style>
